import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    useTheme,
    ButtonBase,
    Slide,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCookies } from "react-cookie";
import dayjs from "dayjs";
import ColoredIcon from "./ColoredIcon";
import AIIcon from "../img/AI.svg";
import ChevronLeft from "../img/Arrow_Left.svg";
import ChevronRight from "../img/Arrow_Right.svg";
import TextField from "./TextField";
import Button from "./Button";
import { hideLanguageSwitcher } from "../utils/languageUtils";
import { APP_MAX_WIDTH } from "../constants/layout";
import AICompletions from "../api/AICompletions";
import Color from "../modules/Color";
import { FONTS } from "../constants/styles";
import { useSwipeToDismiss } from "../hooks/useSwipeToDismiss";

interface ImagePromptModalProps {
    value?: string;
    open: boolean;
    onCancel: () => void;
    onSave: (val?: string) => void;
    brandName: string;
    businessType: string;
    color: Color;

    // Optional hints
    style?: string; // e.g., "minimal, line-art"
    size?: "512x512" | "1024x1024"; // default 1024x1024
}

const MAX_GENERATIONS = 3;

const ImagePromptModal = (props: ImagePromptModalProps) => {
    const {
        open,
        value,
        onCancel,
        onSave,
        brandName,
        businessType,
        color,
        style,
        size = "1024x1024",
    } = props;

    const theme = useTheme();
    const { sheetRef, handleProps } = useSwipeToDismiss(onCancel);
    const [cookies, setCookie] = useCookies([
        "image_generations",
        "image_urls",
    ]);
    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [variants, setVariants] = useState<string[]>([]);
    const [selectedImage, setSelectedImage] = useState<string | undefined>(
        value
    );
    const [generationsLeft, setGenerationsLeft] = useState<number>(
        3 - cookies.image_generations || 3
    );

    useEffect(() => {
        // Restore variants from cookies
        const savedUrls = cookies.image_urls || [];
        setVariants(savedUrls);

        hideLanguageSwitcher();
    }, [cookies.image_urls]);

    useEffect(() => {
        if (cookies.image_generations)
            setGenerationsLeft(3 - cookies.image_generations);
    }, [cookies.image_generations]);

    const generateImage = async () => {
        if (
            prompt.trim().length === 0 ||
            variants.length >= MAX_GENERATIONS ||
            cookies.image_generations >= 3
        ) {
            return;
        }

        setLoading(true);
        try {
            const rgb = { red: color.r, green: color.g, blue: color.b };
            const res = await AICompletions.requestLogoVariant({
                brandName,
                businessType,
                userMessage: prompt.trim(),
                style,
                color: rgb,
                size,
            });

            const variantUrl = res.url || undefined;

            // Update variants and cookies
            if (variantUrl) {
                setVariants((prev) => [...prev, variantUrl]);
                setSelectedImage(variantUrl);
                const currentGenerations = cookies.image_generations || 0;
                const totalGenerations = currentGenerations + 1;
                const updatedUrls = [...(cookies.image_urls || []), variantUrl];

                const expirationDate = dayjs().add(1, "day").toDate();
                setCookie("image_generations", totalGenerations, {
                    path: "/",
                    expires: expirationDate,
                });
                setCookie("image_urls", updatedUrls, {
                    path: "/",
                    expires: expirationDate,
                });
            }
        } catch (err) {
            console.error(`Image generation error:`, err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
            <Box
                ref={sheetRef}
                position="absolute"
                left={0}
                right={0}
                bottom={0}
                height="95%"
                display="flex"
                justifyContent="center"
            >
                {/* The actual sheet */}
                <Stack
                    className="force-dynamic-translation"
                    width="100%"
                    maxWidth={APP_MAX_WIDTH}
                    bgcolor="white"
                    borderRadius="14px 14px 0 0"
                    justifyContent="center"
                    alignItems="center"
                >
                    {/* Drag handle */}
                    <Box
                        sx={{
                            width: 36,
                            height: 4,
                            borderRadius: 2,
                            bgcolor: "rgba(0,0,0,0.15)",
                            mt: 1,
                        }}
                    />
                    {/* Modal header */}
                    <Stack
                        {...handleProps}
                        paddingY={1}
                        spacing={2}
                        width="90%"
                        direction="row"
                        alignItems="center"
                        sx={{ touchAction: "none", cursor: "grab" }}
                    >
                        <ButtonBase
                            disabled={loading}
                            onClick={onCancel}
                            sx={{
                                width: "38px",
                                minWidth: "38px",
                                height: "38px",
                            }}
                        >
                            <ColoredIcon
                                icon={ChevronLeft}
                                size="100%"
                                color="black"
                            />
                        </ButtonBase>
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            width="50px"
                            height="50px"
                            borderRadius="50px"
                            bgcolor={theme.palette.primary.main}
                        >
                            <ColoredIcon
                                icon={AIIcon}
                                color="white"
                                size="34px"
                            />
                        </Stack>
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={700}
                            fontSize="20px"
                            lineHeight="30px"
                        >
                            Logo Generator
                        </Typography>
                    </Stack>
                    {/* Content area */}
                    <Stack
                        direction="column"
                        height="100%"
                        width="100%"
                        overflow="auto"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                        paddingTop={2}
                        bgcolor={alpha(theme.palette.primary.main, 0.05)}
                    >
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            justifyContent="center"
                            alignItems="center"
                            flex={8}
                        >
                            {loading ? (
                                <Box
                                    sx={{
                                        position: "relative",
                                        minWidth: 100,
                                        minHeight: 100,
                                        maxWidth: 200,
                                        maxHeight: 200,
                                        width: "50vw",
                                        height: "50vw",
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        bgcolor: alpha(
                                            theme.palette.primary.main,
                                            0.06
                                        ),
                                        "@keyframes shimmer": {
                                            "0%": {
                                                backgroundPosition:
                                                    "-200% 0",
                                            },
                                            "100%": {
                                                backgroundPosition:
                                                    "200% 0",
                                            },
                                        },
                                        "@keyframes breathe": {
                                            "0%, 100%": {
                                                opacity: 0.4,
                                                transform: "scale(1)",
                                            },
                                            "50%": {
                                                opacity: 1,
                                                transform: "scale(1.08)",
                                            },
                                        },
                                        "&::after": {
                                            content: '""',
                                            position: "absolute",
                                            inset: 0,
                                            background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.08)} 40%, ${alpha(theme.palette.primary.main, 0.15)} 50%, ${alpha(theme.palette.primary.main, 0.08)} 60%, transparent 100%)`,
                                            backgroundSize: "200% 100%",
                                            animation:
                                                "shimmer 2s ease-in-out infinite",
                                        },
                                    }}
                                >
                                    <Stack
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            zIndex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                animation:
                                                    "breathe 2.5s ease-in-out infinite",
                                            }}
                                        >
                                            <ColoredIcon
                                                icon={AIIcon}
                                                color={
                                                    theme.palette.primary.main
                                                }
                                                size="36px"
                                            />
                                        </Box>
                                        <Typography
                                            fontSize="12px"
                                            fontWeight={500}
                                            color="text.secondary"
                                            mt={1}
                                            sx={{
                                                animation:
                                                    "breathe 2.5s ease-in-out infinite",
                                                animationDelay: "0.3s",
                                            }}
                                        >
                                            Generating...
                                        </Typography>
                                    </Stack>
                                </Box>
                            ) : variants.length === 0 ? (
                                <Stack alignItems="center" spacing={1.5}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "14px",
                                            bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.1
                                            ),
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <ColoredIcon
                                            icon={AIIcon}
                                            color={theme.palette.primary.main}
                                            size="28px"
                                        />
                                    </Box>
                                    <Typography
                                        textAlign="center"
                                        fontSize="14px"
                                        fontWeight={500}
                                        color="text.secondary"
                                        maxWidth="280px"
                                        px={3}
                                    >
                                        Describe your perfect logo in the field
                                        below — we'll generate 3 stylish
                                        options based on your idea
                                    </Typography>
                                </Stack>
                            ) : (
                                <Box
                                    minWidth={100}
                                    minHeight={100}
                                    maxWidth={200}
                                    maxHeight={200}
                                    width="50vw"
                                    height="50vw"
                                    borderRadius="12px"
                                    sx={{
                                        backgroundImage: `url(${selectedImage})`,
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: "cover",
                                        cursor: "pointer",
                                    }}
                                />
                            )}
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                            justifyContent="center"
                            flex={2}
                        >
                            {variants.map((variant, index) => (
                                <Box
                                    key={index}
                                    width={100}
                                    height={100}
                                    borderRadius="12px"
                                    sx={{
                                        backgroundImage: `url(${variant})`,
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: "cover",
                                        cursor: "pointer",
                                        border: "2px solid transparent",
                                        boxShadow:
                                            selectedImage === variant
                                                ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}`
                                                : "none",
                                        transition:
                                            "box-shadow 0.2s ease",
                                    }}
                                    onClick={() => setSelectedImage(variant)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                    {/* Prompt area */}
                    <Stack width="90%" paddingY={2} spacing={2}>
                        <Button
                            disabled={!selectedImage || loading}
                            onClick={() => onSave(selectedImage)}
                        >
                            Perfect, that's the one!
                        </Button>
                        <Typography
                            color={theme.palette.primary.main}
                            textAlign="center"
                            fontWeight={600}
                            fontSize="13px"
                        >
                            {generationsLeft === 1
                                ? `${generationsLeft} free generation left`
                                : `${generationsLeft} free generations left`}
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter prompt..."
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            icon={ChevronRight}
                            onClick={generateImage}
                            onKeyDown={(e) =>
                                e.key === "Enter" && generateImage()
                            }
                            disabled={
                                variants.length >= MAX_GENERATIONS ||
                                generationsLeft === 0
                            }
                        />
                    </Stack>
                </Stack>
            </Box>
        </Slide>
    );
};

export default ImagePromptModal;
